import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, ArrowRight, Instagram, Check, Shirt, Users } from "lucide-react";
import { GIRLS_CAMP, SITE } from "../lib/site";

export default function GirlsCamp() {
  const c = GIRLS_CAMP;

  return (
    <div data-testid="girls-camp-page" className="pt-28 md:pt-32">
      {/* RECAP BANNER + VIDEO */}
      <section data-testid="girls-camp-recap" className="pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <div className="bg-primary text-primary-foreground p-6 md:p-8 flex items-center gap-5 flex-wrap">
              <Check size={44} strokeWidth={2.5} className="shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary-foreground/80 mb-2">Wrapped</div>
                <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
                  All Girls Camp: <span className="underline decoration-[3px] underline-offset-[6px]">completed.</span>
                </h2>
                <p className="mt-3 text-sm md:text-base text-primary-foreground/90 max-w-3xl">
                  A huge thank you to every player, family, and coach that made this day special. Here's a quick look at Camp Day 2026.
                </p>
              </div>
            </div>

            <div className="relative mt-4 md:mt-5 bg-foreground overflow-hidden border border-foreground/15" data-testid="girls-camp-recap-video">
              <video
                className="w-full h-auto block"
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_hoops-luxe/artifacts/68db7668ffdbbce2_BeGreatAllGirlsCamp26_Final_V2.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls
              />
            </div>
          </motion.div>
        </div>
      </section>


      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className="h-px w-12 bg-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">All Girls Camp</span>
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-foreground/60">Mankato, MN</span>
          </div>
          <h1 className="font-display uppercase text-5xl md:text-8xl leading-[0.85] tracking-tight">
            All Girls <br /><span className="text-primary">Camp.</span>
          </h1>
          <p className="mt-6 text-foreground/80 text-base md:text-lg max-w-3xl">
            {c.leadHeadline}
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 border-y border-foreground/15">
            {[
              { icon: <Calendar size={16} />, label: "Date", value: c.date },
              { icon: <Clock size={16} />, label: "Sessions", value: "2 Age Groups" },
              { icon: <MapPin size={16} />, label: "Venue", value: "Bethany Lutheran" },
              { icon: <Users size={16} />, label: "Staff", value: "Isaac + 3 D1" },
            ].map((s, i) => (
              <div
                key={s.label}
                className={`py-6 px-4 ${i !== 0 ? "md:border-l border-foreground/15" : ""} ${i % 2 !== 0 ? "border-l border-foreground/15 md:border-l" : ""}`}
              >
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
                  {s.icon} {s.label}
                </div>
                <div className="font-display text-2xl md:text-3xl leading-none mt-2">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESCRIPTION + POSTER */}
      <section className="py-12 md:py-20 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-x-0 gap-y-8 md:gap-12">
          <div className="col-span-12 md:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">The Camp</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mb-8">
              Elite instruction. <br /><span className="text-primary">All girls.</span> <br />One day.
            </h2>
            <p className="text-foreground/80 text-base md:text-lg leading-relaxed">{c.description}</p>
            <ul className="mt-8 space-y-3">
              {c.notes.map((n) => {
                const emphasize = n.toLowerCase().includes("bring their own basketball") || n.toLowerCase().includes("bring your own basketball");
                const isShirt = n.toLowerCase().includes("t-shirt");
                return (
                  <li key={n} className="flex items-start gap-3 text-sm md:text-base">
                    <span className={`mt-1 shrink-0 p-1 ${emphasize || isShirt ? "bg-foreground text-background" : "bg-primary text-primary-foreground"}`}>
                      {isShirt ? <Shirt size={12} strokeWidth={2.5} /> : <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className={emphasize ? "font-bold uppercase tracking-wide" : isShirt ? "font-bold" : ""}>{n}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden border border-foreground/15 bg-foreground"
              data-testid="girls-camp-poster"
            >
              <img src={c.poster} alt="BeGreat All Girls Camp — October 4" className="w-full h-auto block" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* COACHING STAFF */}
      <section className="py-14 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Coaching Staff</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Led by Isaac Fink <br />+ <span className="text-primary">3 former D1 players.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-foreground/15 border border-foreground/15">
            {c.coaches.map((coach, i) => (
              <motion.div
                key={coach.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`girls-camp-coach-${i}`}
                className="bg-background p-6 md:p-8"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-3">
                  {coach.school}
                </div>
                <div className="font-display uppercase text-2xl md:text-3xl leading-none">{coach.name}</div>
                {coach.accolade && (
                  <div className="mt-4 text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-foreground/70">
                    {coach.accolade}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VENUE */}
      <section className="py-12 md:py-20 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Venue</div>
              <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
                {c.location.name}
              </h2>
              <p className="mt-4 text-foreground/70 text-base md:text-lg">{c.location.address}</p>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <a
                href={c.location.mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="girls-camp-maps"
                className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-primary transition-colors"
              >
                <MapPin size={14} /> Open In Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SESSIONS / REGISTRATION */}
      <section className="py-16 md:py-24 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Registration</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Pick your <span className="text-primary">session.</span>
              </h2>
            </div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-background/60 max-w-xs text-right">
              Sunday, October 4 · Bring your own basketball
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-background/15">
            {c.sessions.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`girls-camp-session-${s.id}`}
                className={`relative bg-foreground p-8 md:p-10 group transition-colors ${
                  s.soldOut ? "opacity-90" : "hover:bg-primary hover:text-primary-foreground"
                }`}
              >
                {s.soldOut && (
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] z-10" data-testid={`girls-camp-soldout-${s.id}`}>
                    Sold Out
                  </div>
                )}
                {!s.soldOut && s.spotsLabel && (
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] z-10" data-testid={`girls-camp-spots-${s.id}`}>
                    {s.spotsLabel}
                  </div>
                )}

                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary group-hover:text-primary-foreground/80 mb-4">
                  {s.who}
                </div>
                <div className="font-display uppercase text-4xl md:text-5xl leading-none">{s.grades}</div>
                <div className="mt-5 flex items-center gap-2 text-base">
                  <Clock size={14} className={`${s.soldOut ? "text-primary" : "text-primary group-hover:text-primary-foreground"}`} /> {s.time}
                </div>

                <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.3em] text-background/60 group-hover:text-primary-foreground/70 mb-3">
                  {s.focusHeading}
                </div>
                <ul className="space-y-2">
                  {s.focus.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <span className="w-1.5 h-1.5 bg-primary group-hover:bg-primary-foreground" /> {f}
                    </li>
                  ))}
                </ul>

                {s.soldOut ? (
                  <div
                    data-testid={`girls-camp-register-${s.id}`}
                    className="mt-8 inline-flex items-center gap-2 bg-background/20 text-background/60 px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] cursor-not-allowed"
                  >
                    Registration Closed
                  </div>
                ) : (
                  <a
                    href={s.regUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`girls-camp-register-${s.id}`}
                    className="mt-8 inline-flex items-center gap-2 bg-background text-foreground px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-foreground hover:text-background transition-colors group-hover:bg-foreground group-hover:text-background"
                  >
                    Sign Up {s.grades.replace("Grades ", "")} <ArrowRight size={14} />
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center text-xs font-bold uppercase tracking-[0.3em] text-background/50">
            Every registered player receives a BeGreat T-shirt
          </div>
        </div>
      </section>

      {/* INSTAGRAM PROMO */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 items-center">
          <h3 className="col-span-12 md:col-span-8 font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
            Follow {SITE.instagramHandle} <br /><span className="text-primary">for camp updates.</span>
          </h3>
          <div className="col-span-12 md:col-span-4 md:text-right flex md:justify-end gap-3 flex-wrap">
            <a href={SITE.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 border-2 border-foreground text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-primary hover:border-primary hover:text-primary-foreground transition-colors">
              <Instagram size={14} /> BeGreat
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
